import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import {
  XMarkIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  EllipsisVerticalIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { StateBadge } from './StateBadge';
import { useMasterDocumentTranslation } from './i18n';
import { useMasterDocumentFormatters } from './formatters';
import type {
  DocumentModalListConfig,
  DocumentModalListButtonConfig,
  DocumentModalListMenuItemConfig,
  DocumentModalListColumnConfig,
} from './template/MasterDocumentTemplate.types';
import type { StateBadgeConfig } from './MasterDocument.types';

// ─── Types ─────────────────────────────────────────────────

export interface DocumentModalListProps {
  open: boolean;
  onClose: () => void;
  config: DocumentModalListConfig;
  /** UUID del documento desde el cual se abre el modal. */
  documentId?: string;
  /** UUID de la línea (solo para context: 'line'). */
  lineId?: string;
  /** Params adicionales del contexto del documento (ej: headerValues snapshot). */
  contextParams?: Record<string, unknown>;
  /**
   * Función para realizar llamadas HTTP.
   * Recibe method, path relativo al baseUrl y body opcional.
   */
  fetcher: (method: string, path: string, body?: unknown) => Promise<unknown>;
  /** Formateador de moneda (inyectado por el adaptador). */
  formatCurrency?: (value: number) => string;
  /** Formateador de fecha (inyectado por el adaptador). */
  formatDate?: (value: string) => string;
}

// ─── Helpers ───────────────────────────────────────────────

function interpolatePath(
  path: string,
  vars: Record<string, string | undefined>,
): string {
  return path.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? '');
}

function formatValue(
  value: unknown,
  format: string | undefined,
  formatCurrency: ((v: number) => string) | undefined,
  formatDate: ((v: string) => string) | undefined,
  formatNumber: (v: number) => string,
): string {
  if (value == null) return '';
  if (format === 'currency' && formatCurrency && typeof value === 'number') {
    return formatCurrency(value);
  }
  if (format === 'date' && formatDate && typeof value === 'string') {
    return formatDate(value);
  }
  if (format === 'number' && typeof value === 'number') {
    return formatNumber(value);
  }
  return String(value);
}

function applyDisplayTemplate(template: string, row: Record<string, unknown>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(row[key] ?? ''));
}

function getCellValue(
  row: Record<string, unknown>,
  col: DocumentModalListColumnConfig,
  formatCurrency: ((v: number) => string) | undefined,
  formatDate: ((v: string) => string) | undefined,
  formatNumber: (v: number) => string,
): string {
  if (col.displayTemplate) return applyDisplayTemplate(col.displayTemplate, row);
  const raw = row[col.dataKey ?? col.key];
  return formatValue(raw, col.format, formatCurrency, formatDate, formatNumber);
}

// ─── ConfirmDialog — dialog de confirmación inline ─────────

export interface ConfirmDialogProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Deshabilita el botón de confirmación.
   * Útil cuando la confirmación depende de un campo obligatorio del contenido (children),
   * por ejemplo un modal de anulación que exige seleccionar un "motivo".
   * No afecta al botón de cancelar.
   * @default false
   */
  confirmDisabled?: boolean;
  /**
   * Contenido opcional a renderizar entre el mensaje y los botones
   * (por ejemplo el campo "motivo" del que depende `confirmDisabled`).
   */
  children?: ReactNode;
}

export function ConfirmDialog({
  message,
  onConfirm,
  onCancel,
  confirmDisabled = false,
  children,
}: ConfirmDialogProps) {
  const { t } = useMasterDocumentTranslation();
  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onCancel}
      />
      <div
        className={clsx(
          'relative z-10 w-[calc(100%-32px)] max-w-sm',
          'bg-bg-primary dark:bg-dark-bg-primary',
          'rounded-2xl shadow-xl dark:border dark:border-dark-border-primary',
          'p-6 flex flex-col gap-4',
        )}
        role="alertdialog"
        aria-modal="true"
      >
        <p className="text-sm text-content-primary dark:text-dark-content-primary">{message}</p>
        {children}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-semibold',
              'border border-border-primary text-content-secondary',
              'hover:bg-background-secondary transition-colors',
              'dark:border-dark-border-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
            )}
          >
            {t('master_document.chrome.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-semibold',
              'bg-red-600 text-white hover:bg-red-700 transition-colors',
              'dark:bg-red-700 dark:hover:bg-red-600',
              confirmDisabled && 'opacity-50 cursor-not-allowed hover:bg-red-600 dark:hover:bg-red-700',
            )}
          >
            {t('master_document.chrome.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── RowContextMenu — menú de acciones por registro ────────

interface RowContextMenuProps {
  items: DocumentModalListMenuItemConfig[];
  onSelect: (item: DocumentModalListMenuItemConfig) => void;
  onClose: () => void;
  position: { x: number; y: number };
}

function RowContextMenu({ items, onSelect, onClose, position }: RowContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  // Adjust position to avoid going off-screen
  const style: React.CSSProperties = {
    position: 'fixed',
    top: position.y,
    left: position.x,
    zIndex: 410,
  };

  return (
    <div
      ref={ref}
      style={style}
      className={clsx(
        'min-w-[160px] rounded-xl shadow-lg',
        'bg-bg-primary dark:bg-dark-bg-secondary',
        'border border-border-primary dark:border-dark-border-primary',
        'py-1',
        'animate-[fadeIn_100ms_ease-out]',
      )}
      role="menu"
    >
      {items.map((item) => (
        <div key={item.key}>
          {item.separator && (
            <div className="my-1 border-t border-border-primary dark:border-dark-border-primary" />
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => onSelect(item)}
            className={clsx(
              'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
              'transition-colors duration-100',
              item.danger
                ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                : 'text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-primary',
            )}
          >
            {item.label}
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── ListTable — vista de tabla ─────────────────────────────

interface ListTableProps {
  rows: Record<string, unknown>[];
  columns: DocumentModalListColumnConfig[];
  contextMenu?: DocumentModalListMenuItemConfig[];
  onContextMenuOpen: (row: Record<string, unknown>, position: { x: number; y: number }) => void;
  formatCurrency?: (v: number) => string;
  formatDate?: (v: string) => string;
  emptyMessage?: string;
}

function ListTable({
  rows,
  columns,
  contextMenu,
  onContextMenuOpen,
  formatCurrency,
  formatDate,
  emptyMessage,
}: ListTableProps) {
  const { t } = useMasterDocumentTranslation();
  const { formatNumber } = useMasterDocumentFormatters();
  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-content-tertiary dark:text-dark-content-secondary">
          {emptyMessage ?? t('master_document.list.noRecords')}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-border-primary dark:border-dark-border-primary">
            {columns.map((col) => (
              <th
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
                className={clsx(
                  'px-3 py-2.5 text-xs font-semibold uppercase tracking-wide',
                  'text-content-tertiary dark:text-dark-content-secondary',
                  'whitespace-nowrap',
                  col.align === 'right' && 'text-right',
                  col.align === 'center' && 'text-center',
                  (!col.align || col.align === 'left') && 'text-left',
                )}
              >
                {col.header}
              </th>
            ))}
            {contextMenu && contextMenu.length > 0 && (
              <th className="w-10 px-2 py-2.5" aria-label={t('master_document.list.rowActions')} />
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={idx}
              className={clsx(
                'border-b border-border-primary dark:border-dark-border-primary',
                'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
                'transition-colors duration-100',
              )}
            >
              {columns.map((col) => {
                const rawValue = row[col.dataKey ?? col.key];
                const badge = col.badgeMap?.[String(rawValue ?? '')];
                const displayText = getCellValue(row, col, formatCurrency, formatDate, formatNumber);

                return (
                  <td
                    key={col.key}
                    className={clsx(
                      'px-3 py-2.5',
                      col.mono && 'tabular-nums text-xs',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      (!col.align || col.align === 'left') && 'text-left',
                      'text-content-primary dark:text-dark-content-primary',
                    )}
                  >
                    {badge ? (
                      <StateBadge label={badge.label} bg={badge.bg} color={badge.color} />
                    ) : (
                      displayText
                    )}
                  </td>
                );
              })}
              {contextMenu && contextMenu.length > 0 && (
                <td className="px-2 py-2">
                  <button
                    type="button"
                    aria-label={t('master_document.list.rowOptions')}
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      onContextMenuOpen(row, { x: rect.left - 160, y: rect.bottom + 4 });
                    }}
                    className={clsx(
                      'p-1 rounded-md',
                      'text-content-tertiary dark:text-dark-content-secondary',
                      'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
                      'transition-colors duration-100',
                    )}
                  >
                    <EllipsisVerticalIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── CardGrid — vista de cards ──────────────────────────────

interface CardGridProps {
  rows: Record<string, unknown>[];
  config: NonNullable<DocumentModalListConfig['card']>;
  contextMenu?: DocumentModalListMenuItemConfig[];
  onContextMenuOpen: (row: Record<string, unknown>, position: { x: number; y: number }) => void;
  formatCurrency?: (v: number) => string;
  formatDate?: (v: string) => string;
  emptyMessage?: string;
}

function CardGrid({
  rows,
  config,
  contextMenu,
  onContextMenuOpen,
  formatCurrency,
  formatDate,
  emptyMessage,
}: CardGridProps) {
  const { t } = useMasterDocumentTranslation();
  const { formatNumber } = useMasterDocumentFormatters();
  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-content-tertiary dark:text-dark-content-secondary">
          {emptyMessage ?? t('master_document.list.noRecords')}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-1">
      {rows.map((row, idx) => {
        const title = String(row[config.titleField] ?? '');
        const subtitle = config.subtitleField ? String(row[config.subtitleField] ?? '') : undefined;
        const badgeRaw = config.badgeField ? String(row[config.badgeField] ?? '') : undefined;
        const badge: StateBadgeConfig | undefined =
          badgeRaw && config.badgeMap ? config.badgeMap[badgeRaw] : undefined;

        return (
          <div
            key={idx}
            className={clsx(
              'relative rounded-xl p-4',
              'bg-bg-primary dark:bg-dark-bg-secondary',
              'border border-border-primary dark:border-dark-border-primary',
              'hover:border-primary-custom-300 dark:hover:border-primary-custom-600',
              'transition-colors duration-150',
            )}
          >
            {/* Card header */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-content-primary dark:text-dark-content-primary truncate">
                  {title}
                </p>
                {subtitle && (
                  <p className="text-xs text-content-secondary dark:text-dark-content-secondary mt-0.5 truncate">
                    {subtitle}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {badge && (
                  <StateBadge label={badge.label} bg={badge.bg} color={badge.color} />
                )}
                {contextMenu && contextMenu.length > 0 && (
                  <button
                    type="button"
                    aria-label={t('master_document.list.rowOptions')}
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      onContextMenuOpen(row, { x: rect.left - 160, y: rect.bottom + 4 });
                    }}
                    className={clsx(
                      'p-1 rounded-md',
                      'text-content-tertiary dark:text-dark-content-secondary',
                      'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
                      'transition-colors duration-100',
                    )}
                  >
                    <EllipsisVerticalIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>

            {/* Card fields */}
            {config.fields && config.fields.length > 0 && (
              <dl className="space-y-1 mt-3 pt-3 border-t border-border-primary dark:border-dark-border-primary">
                {config.fields.map((field) => {
                  const raw = row[field.dataKey ?? field.key];
                  const text = formatValue(raw, field.format, formatCurrency, formatDate, formatNumber);
                  return (
                    <div key={field.key} className="flex justify-between gap-2">
                      {field.label && (
                        <dt className="text-xs text-content-tertiary dark:text-dark-content-secondary shrink-0">
                          {field.label}
                        </dt>
                      )}
                      <dd
                        className={clsx(
                          'text-xs text-right',
                          field.bold && 'font-semibold',
                          field.accent
                            ? 'text-primary-custom-600 dark:text-primary-custom-400'
                            : 'text-content-primary dark:text-dark-content-primary',
                        )}
                      >
                        {text}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── DocumentModalList — componente principal ───────────────

/**
 * Modal de lista interna del documento.
 * Muestra registros relacionados a un documento con toolbar configurable,
 * menú de contexto por registro y soporte para vista tabla o cards.
 *
 * Se define completamente a través del JSON template en `ActionRegistryEntry.modalList`.
 *
 * @example
 * ```tsx
 * <DocumentModalList
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   config={template.actionRegistry['ver_entregas'].modalList}
 *   documentId={state.documentId}
 *   fetcher={adapter.genericFetch}
 * />
 * ```
 */
export function DocumentModalList({
  open,
  onClose,
  config,
  documentId,
  lineId,
  contextParams,
  fetcher,
  formatCurrency,
  formatDate,
}: DocumentModalListProps) {
  const { t } = useMasterDocumentTranslation();
  const [maximized, setMaximized] = useState(config.maximized ?? false);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = config.pageSize ?? 20;

  // Per-button loading state for toolbar
  const [buttonLoading, setButtonLoading] = useState<Record<string, boolean>>({});

  // Context menu state
  const [menuRow, setMenuRow] = useState<Record<string, unknown> | null>(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  // Confirmation dialog state
  const [confirm, setConfirm] = useState<{
    message: string;
    onConfirm: () => void;
  } | null>(null);

  // ── Context variable map for path interpolation ──
  const contextVars = useCallback(
    (extraVars?: Record<string, string>): Record<string, string | undefined> => ({
      documentId: documentId,
      lineId: lineId,
      ...Object.fromEntries(
        Object.entries(contextParams ?? {}).map(([k, v]) => [k, String(v ?? '')]),
      ),
      ...extraVars,
    }),
    [documentId, lineId, contextParams],
  );

  // ── Load list data ────────────────────────────────
  const loadData = useCallback(
    async (currentPage: number) => {
      setLoading(true);
      setError(null);
      try {
        const path = interpolatePath(
          config.dataEndpoint.path,
          contextVars(),
        );
        const body =
          config.dataEndpoint.method !== 'GET'
            ? {
                page: currentPage,
                pageSize,
                ...config.dataEndpoint.params,
              }
            : undefined;

        const result = await fetcher(config.dataEndpoint.method, path, body);
        const data = result as { rows?: Record<string, unknown>[]; totalCount?: number } | Record<string, unknown>[];

        if (Array.isArray(data)) {
          setRows(data);
          setTotalCount(data.length);
        } else {
          setRows(data.rows ?? []);
          setTotalCount(data.totalCount ?? data.rows?.length ?? 0);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t('master_document.list.loadError'));
      } finally {
        setLoading(false);
      }
    },
    [config.dataEndpoint, contextVars, fetcher, pageSize, t],
  );

  useEffect(() => {
    if (open) {
      setPage(1);
      loadData(1);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset maximized when config changes
  useEffect(() => {
    if (open) setMaximized(config.maximized ?? false);
  }, [open, config.maximized]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !confirm) onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose, confirm]);

  if (!open) return null;

  // ── Toolbar button handler ────────────────────────
  async function handleToolbarButton(btn: DocumentModalListButtonConfig) {
    if (btn.requiresConfirmation) {
      setConfirm({
        message: btn.confirmationMessage ?? t('master_document.list.confirmAction'),
        onConfirm: () => {
          setConfirm(null);
          void executeToolbarButton(btn);
        },
      });
      return;
    }
    await executeToolbarButton(btn);
  }

  async function executeToolbarButton(btn: DocumentModalListButtonConfig) {
    setButtonLoading((prev) => ({ ...prev, [btn.key]: true }));
    try {
      const path = interpolatePath(btn.endpoint.path, contextVars());
      await fetcher(btn.endpoint.method, path, btn.endpoint.params);
      if (btn.reloadAfter) await loadData(page);
    } catch {
      // Errors shown inline; let consumer handle globally via adapter.onError
    } finally {
      setButtonLoading((prev) => ({ ...prev, [btn.key]: false }));
    }
  }

  // ── Context menu item handler ─────────────────────
  function handleContextMenuOpen(row: Record<string, unknown>, pos: { x: number; y: number }) {
    setMenuRow(row);
    setMenuPosition(pos);
  }

  function handleContextMenuClose() {
    setMenuRow(null);
  }

  async function handleMenuItemSelect(item: DocumentModalListMenuItemConfig) {
    setMenuRow(null);
    if (!menuRow) return;

    const recordIdField = config.recordIdField ?? 'id';
    const recordId = String(menuRow[recordIdField] ?? '');

    if (item.requiresConfirmation) {
      setConfirm({
        message: item.confirmationMessage ?? t('master_document.list.confirmAction'),
        onConfirm: () => {
          setConfirm(null);
          void executeMenuAction(item, recordId);
        },
      });
      return;
    }
    await executeMenuAction(item, recordId);
  }

  async function executeMenuAction(item: DocumentModalListMenuItemConfig, recordId: string) {
    try {
      const path = interpolatePath(item.endpoint.path, contextVars({ recordId }));
      await fetcher(item.endpoint.method, path, item.endpoint.params);
      if (item.reloadAfter) await loadData(page);
    } catch {
      // Errors handled globally
    }
  }

  // ── Pagination ────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  function handlePageChange(newPage: number) {
    setPage(newPage);
    void loadData(newPage);
  }

  // ── Sizes ─────────────────────────────────────────
  const displayMode = config.displayMode ?? 'list';

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={clsx(
          'fixed z-[301] flex flex-col',
          'bg-bg-primary dark:bg-dark-bg-primary',
          'shadow-xl',
          'dark:border dark:border-dark-border-primary',
          'animate-[fadeIn_150ms_ease-out]',
          maximized
            ? 'inset-2 rounded-2xl'
            : [
                'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                'w-[calc(100%-32px)] max-w-[75vw]',
                'max-h-[80vh] rounded-2xl',
              ],
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dml-modal-title"
      >
        {/* ── Header ── */}
        <div
          className={clsx(
            'flex items-center justify-between px-5 py-3.5 shrink-0',
            'border-b border-border-primary dark:border-dark-border-primary',
          )}
        >
          <h2
            id="dml-modal-title"
            className="text-[15px] font-bold text-content-primary dark:text-dark-content-primary truncate"
          >
            {config.title}
          </h2>

          <div className="flex items-center gap-1">
            {/* Reload */}
            <button
              type="button"
              onClick={() => void loadData(page)}
              disabled={loading}
              aria-label={t('master_document.list.reload')}
              className={clsx(
                'p-1.5 rounded-md',
                'text-content-secondary dark:text-dark-content-secondary',
                'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
                'transition-colors duration-150',
                'disabled:opacity-40',
              )}
            >
              <ArrowPathIcon className={clsx('w-4 h-4', loading && 'animate-spin')} aria-hidden="true" />
            </button>

            {/* Maximize toggle */}
            <button
              type="button"
              onClick={() => setMaximized((m) => !m)}
              aria-label={maximized ? t('master_document.chrome.restoreSize') : t('master_document.chrome.maximize')}
              className={clsx(
                'p-1.5 rounded-md',
                'text-content-secondary dark:text-dark-content-secondary',
                'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
                'transition-colors duration-150',
              )}
            >
              {maximized ? (
                <ArrowsPointingInIcon className="w-4 h-4" aria-hidden="true" />
              ) : (
                <ArrowsPointingOutIcon className="w-4 h-4" aria-hidden="true" />
              )}
            </button>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              aria-label={t('master_document.chrome.close')}
              className={clsx(
                'p-1.5 rounded-md',
                'text-content-secondary dark:text-dark-content-secondary',
                'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
                'transition-colors duration-150',
                'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
                'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              )}
            >
              <XMarkIcon className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* ── Toolbar ── */}
        {config.toolbar && config.toolbar.buttons.length > 0 && (
          <div
            className={clsx(
              'flex items-center gap-2 px-4 py-2.5 shrink-0',
              'border-b border-border-primary dark:border-dark-border-primary',
              'bg-background-secondary/40 dark:bg-dark-bg-secondary/40',
            )}
          >
            {config.toolbar.buttons.map((btn) => {
              const isLoading = buttonLoading[btn.key] ?? false;
              return (
                <button
                  key={btn.key}
                  type="button"
                  onClick={() => void handleToolbarButton(btn)}
                  disabled={isLoading}
                  className={clsx(
                    'inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-sm font-semibold',
                    'transition-colors duration-150',
                    'disabled:opacity-50 disabled:pointer-events-none',
                    btn.variant === 'primary' && [
                      'bg-primary-inverse-background text-white',
                      'hover:bg-primary-custom-700',
                      'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
                    ],
                    btn.variant === 'danger' && [
                      'bg-red-600 text-white hover:bg-red-700',
                      'dark:bg-red-700 dark:hover:bg-red-600',
                    ],
                    (!btn.variant || btn.variant === 'secondary') && [
                      'border border-border-primary text-content-secondary',
                      'hover:bg-background-secondary',
                      'dark:border-dark-border-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
                    ],
                    btn.variant === 'ghost' && [
                      'text-content-secondary hover:bg-background-secondary',
                      'dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
                    ],
                  )}
                >
                  {isLoading && (
                    <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                  )}
                  {btn.label}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3">
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-primary-custom-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              <button
                type="button"
                onClick={() => void loadData(page)}
                className={clsx(
                  'px-4 py-2 rounded-md text-sm font-semibold',
                  'border border-border-primary text-content-secondary',
                  'hover:bg-background-secondary transition-colors',
                  'dark:border-dark-border-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
                )}
              >
                {t('master_document.chrome.retry')}
              </button>
            </div>
          ) : displayMode === 'card' && config.card ? (
            <CardGrid
              rows={rows}
              config={config.card}
              contextMenu={config.contextMenu}
              onContextMenuOpen={handleContextMenuOpen}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              emptyMessage={config.emptyMessage}
            />
          ) : (
            <ListTable
              rows={rows}
              columns={config.columns ?? []}
              contextMenu={config.contextMenu}
              onContextMenuOpen={handleContextMenuOpen}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              emptyMessage={config.emptyMessage}
            />
          )}
        </div>

        {/* ── Footer: pagination ── */}
        {totalPages > 1 && (
          <div
            className={clsx(
              'flex items-center justify-between px-4 py-2.5 shrink-0',
              'border-t border-border-primary dark:border-dark-border-primary',
            )}
          >
            <span className="text-xs text-content-tertiary dark:text-dark-content-secondary">
              {t('master_document.list.recordCount', { count: totalCount })}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1 || loading}
                className={clsx(
                  'px-2.5 py-1 rounded-md text-xs font-semibold',
                  'border border-border-primary text-content-secondary',
                  'hover:bg-background-secondary transition-colors',
                  'disabled:opacity-40 disabled:pointer-events-none',
                  'dark:border-dark-border-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
                )}
              >
                {t('master_document.list.previousShort')}
              </button>
              <span className="text-xs text-content-secondary dark:text-dark-content-secondary px-2">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages || loading}
                className={clsx(
                  'px-2.5 py-1 rounded-md text-xs font-semibold',
                  'border border-border-primary text-content-secondary',
                  'hover:bg-background-secondary transition-colors',
                  'disabled:opacity-40 disabled:pointer-events-none',
                  'dark:border-dark-border-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
                )}
              >
                {t('master_document.list.nextShort')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Context menu ── */}
      {menuRow && config.contextMenu && config.contextMenu.length > 0 && (
        <RowContextMenu
          items={config.contextMenu}
          position={menuPosition}
          onSelect={(item) => void handleMenuItemSelect(item)}
          onClose={handleContextMenuClose}
        />
      )}

      {/* ── Confirmation dialog ── */}
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}
